import { Injectable, NgZone } from '@angular/core';
import cloneDeep from 'lodash/cloneDeep';
import { BehaviorSubject, Observable, Subject, catchError, map, of, tap } from 'rxjs';
import { PromptService } from '../../components/prompt/prompt.service';
import { SavedViewsProviderService } from '../../data-providers/api-providers/saved-views-provider/saved-views-provider.service';
import { TranslationPipe } from '../../features/translate/translation.pipe';
import { EIntercomEvent } from '../../services/intercom-service-consts';
import { IntercomService } from '../../services/intercom.service';
import { ResponseErrorService } from '../errors/response-error.service';
import { TColumn } from '../site/site-table/columns/column.model';
import { UserService } from '../user/user.service';
import { checkCustomWorkspaceId } from '../workspace/workspace';
import { CompareColumnsWithSavedViewService } from './compare-columns-with-saved-view.service';
import { ESavedView } from './models/saved-view.enum';
import { TSavedView } from './models/saved-view.model';
import { TSavedViews, TViewShort, TViewsByWorkspace } from './models/saved-views.model';
import { fixView } from './utils/fix-view';

@Injectable({
  providedIn: 'root',
})
export class SavedViewsService {
  private savedViewsList: {
    [workspaceId: string]: TSavedViews;
  } = {};

  private individualViews: {
    [viewId: string]: TSavedView;
  } = {};

  private readonly _viewsChange$ = new BehaviorSubject<TViewsByWorkspace>(this.savedViewsList);
  readonly viewsChange$ = this._viewsChange$.asObservable();
  private selectedViewId: {
    [workspaceId: string]: string;
  } = {};
  private activeViewId: string;

  private createInputSubject = new Subject<string>();
  createInputEvent$ = this.createInputSubject.asObservable();

  constructor(
    private savedViewsProviderService: SavedViewsProviderService,
    private userService: UserService,
    private responseErrorService: ResponseErrorService,
    private ngZone: NgZone,
    private compareColumnsWithSavedViewService: CompareColumnsWithSavedViewService,
    private promptService: PromptService,
    private translationPipe: TranslationPipe,
    private intercomService: IntercomService,
  ) {}

  fetchSavedViews(workspaceId: string): Observable<TSavedViews> {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    if (this.savedViewsList[requestWorkspaceId]) {
      if (!this.selectedViewId[requestWorkspaceId]) {
        this.setDefaultSelectedView(this.savedViewsList[requestWorkspaceId], requestWorkspaceId);
      }

      return of(this.savedViewsList[requestWorkspaceId]);
    }

    return this.savedViewsProviderService.getViews(requestWorkspaceId).pipe(
      map((views) => {
        const fixedView: Partial<TSavedViews> = this.fixWorkspaceViews(views, requestWorkspaceId);

        this.setDefaultSelectedView(fixedView, requestWorkspaceId);

        this.savedViewsList[requestWorkspaceId] = fixedView as TSavedViews;
        this._viewsChange$.next(this.savedViewsList);

        return fixedView as TSavedViews;
      }),
    );
  }

  getSavedViews(workspaceId: string): TSavedViews {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    return this.savedViewsList[requestWorkspaceId];
  }

  fetchView({
    workspaceId,
    viewId,
  }: {
    workspaceId: string;
    viewId: string;
  }): Observable<TSavedView> {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    if (this.individualViews[viewId]) {
      return of(fixView({ view: this.individualViews[viewId] }));
    }

    return this.savedViewsProviderService.getView(requestWorkspaceId, viewId).pipe(
      map((view) => {
        this.individualViews[viewId] = fixView({ view });

        return this.individualViews[viewId];
      }),
    );
  }

  getView(viewId: string): TSavedView {
    return cloneDeep(this.individualViews[viewId]);
  }

  createView(
    view: Partial<TSavedView>,
    workspaceId: string,
    duplicatingView?: boolean,
  ): Observable<TSavedView> {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    return this.savedViewsProviderService.createView(requestWorkspaceId, view).pipe(
      tap((newView) => {
        this.individualViews[newView.id] = newView;

        if (view.viewType === ESavedView.PERSONAL) {
          this.savedViewsList[requestWorkspaceId].personal.views.push({
            id: newView.id,
            name: newView.name,
          });
        } else {
          this.savedViewsList[requestWorkspaceId].shared.views.push({
            id: newView.id,
            name: newView.name,
          });
        }

        if (!duplicatingView) {
          this.setSelectedViewId(requestWorkspaceId, newView.id);
        }

        this.intercomService.trackIntercomEvent(EIntercomEvent.CREATE_SAVED_VIEW);

        this._viewsChange$.next({ ...this.savedViewsList });

        this.ngZone.runOutsideAngular(() => {
          window.requestAnimationFrame(() => {
            this.createInputSubject.next(newView.id);
          });
        });
      }),
      catchError((error) =>
        this.responseErrorService.handleRequestError(error, 'Failed to create saved view'),
      ),
    );
  }

  editSavedView(workspaceId: string, view: TSavedView): Observable<TSavedView> {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    return this.savedViewsProviderService.editView(requestWorkspaceId, view).pipe(
      tap((newView) => {
        this.updateView(newView, requestWorkspaceId);
      }),
    );
  }

  tryUpdateActiveView(workspaceId: string, columns: TColumn[]): void {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    if (!this.savedViewsList[requestWorkspaceId]) {
      return;
    }

    if (this.checkIfViewIsActive(requestWorkspaceId, columns)) {
      this.setActiveViewId(this.selectedViewId[requestWorkspaceId]);
    } else {
      this.setActiveViewId(null);
    }
  }

  checkIfViewIsActive(workspaceId: string, columns: TColumn[]): boolean {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    if (!this.savedViewsList[requestWorkspaceId]) {
      return false;
    }

    const selectedViewId = this.selectedViewId[requestWorkspaceId];
    const selectedView = this.getView(selectedViewId);

    if (!selectedView) {
      return false;
    }

    return this.compareColumnsWithSavedViewService.compareColumns(
      selectedView.columns,
      columns,
      workspaceId,
    );
  }

  setDefaultView(workspaceId: string, viewId: string, type: ESavedView): Observable<TSavedViews> {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    return this.savedViewsProviderService.setDefaultView(requestWorkspaceId, viewId, type).pipe(
      tap(() => {
        if (type === ESavedView.PERSONAL) {
          this.savedViewsList[requestWorkspaceId].personal.defaultViewId = viewId;
        } else {
          this.savedViewsList[requestWorkspaceId].shared.sharedDefaultViewId = viewId;
        }

        this._viewsChange$.next(this.savedViewsList);
      }),
    );
  }

  deleteDefaultView(workspaceId: string, type: ESavedView): Observable<null> {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    return this.savedViewsProviderService.deleteDefaultView(requestWorkspaceId, type).pipe(
      tap(() => {
        if (type === ESavedView.PERSONAL) {
          this.savedViewsList[requestWorkspaceId].personal.defaultViewId = null;
        } else {
          this.savedViewsList[requestWorkspaceId].shared.sharedDefaultViewId = null;
        }

        this._viewsChange$.next(this.savedViewsList);
      }),
    );
  }

  reorderViews(
    workspaceId: string,
    viewId: string,
    viewType: ESavedView,
    newOrder: number,
  ): Observable<null> {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    return this.savedViewsProviderService
      .reorderViews(requestWorkspaceId, viewId, viewType, newOrder)
      .pipe(
        tap((response) => {
          this.savedViewsList[requestWorkspaceId] = this.fixWorkspaceViews(
            response,
            requestWorkspaceId,
          );

          this._viewsChange$.next(this.savedViewsList);
        }),
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to reorder saved views'),
        ),
      );
  }

  deleteView(viewId: string, workspaceId: string, type: ESavedView): void {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    this.savedViewsProviderService
      .deleteView(viewId, requestWorkspaceId)
      .pipe(
        tap(() => {
          delete this.individualViews[viewId];

          if (type === ESavedView.PERSONAL) {
            const itemIndex = this.savedViewsList[requestWorkspaceId].personal.views.findIndex(
              (view) => view.id === viewId,
            );

            this.savedViewsList[requestWorkspaceId].personal.views.splice(itemIndex, 1);

            // Delete if it was default
            if (this.savedViewsList[requestWorkspaceId].personal.defaultViewId === viewId) {
              this.deleteDefaultView(requestWorkspaceId, ESavedView.PERSONAL).subscribe();
            }
          } else {
            const itemIndex = this.savedViewsList[requestWorkspaceId].shared.views.findIndex(
              (view) => view.id === viewId,
            );

            this.savedViewsList[requestWorkspaceId].shared.views.splice(itemIndex, 1);

            if (this.savedViewsList[requestWorkspaceId].shared.sharedDefaultViewId === viewId) {
              this.deleteDefaultView(requestWorkspaceId, ESavedView.SHARED).subscribe();
            }
          }

          if (this.selectedViewId[requestWorkspaceId] === viewId) {
            this.setSelectedViewId(requestWorkspaceId, null);
          }

          this._viewsChange$.next(this.savedViewsList);
        }),
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to delete saved view'),
        ),
      )
      .subscribe();
  }

  renameView(viewId: string, newName: string, workspaceId: string): Observable<TSavedView> {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    return this.savedViewsProviderService
      .editView(requestWorkspaceId, { name: newName, id: viewId })
      .pipe(
        tap((view) => {
          this.updateView(view, requestWorkspaceId);
        }),
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to rename saved view'),
        ),
      );
  }

  updateDefaultPersonalView(viewId: string, workspaceId: string): void {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    this.savedViewsList[requestWorkspaceId].personal.defaultViewId = viewId;

    this._viewsChange$.next(this.savedViewsList);
  }

  updateDefaultSharedView(viewId: string, workspaceId: string): void {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    this.savedViewsList[requestWorkspaceId].shared.sharedDefaultViewId = viewId;

    this._viewsChange$.next(this.savedViewsList);
  }

  private updateView(view: TSavedView, workspaceId: string): void {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    this.individualViews[view.id] = view;

    const shortView = this.findView(view.id, requestWorkspaceId);

    shortView.name = view.name;

    this._viewsChange$.next(this.savedViewsList);
  }

  findView(viewId: string, workspaceId: string): TViewShort {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    if (!this.savedViewsList[requestWorkspaceId]) {
      return null;
    }

    const foundView = this.savedViewsList[requestWorkspaceId].personal.views.find(
      (view) => viewId === view.id,
    );

    if (foundView) {
      return foundView;
    }

    return this.savedViewsList[requestWorkspaceId].shared.views.find((view) => viewId === view.id);
  }

  getSelectedViewId(workspaceId: string): string {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    return this.selectedViewId[requestWorkspaceId];
  }

  setSelectedViewId(workspaceId: string, viewId: string): void {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    this.selectedViewId[requestWorkspaceId] = viewId;
    this._viewsChange$.next(this.savedViewsList);
  }

  clearWorkspaceSavedViews(workspaceId: string): void {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    if (this.individualViews) {
      this.savedViewsList[requestWorkspaceId].personal.views.forEach((view) => {
        delete this.individualViews[view.id];
      });

      this.savedViewsList[requestWorkspaceId].shared.views.forEach((view) => {
        delete this.individualViews[view.id];
      });
    }
    this.savedViewsList = { ...this.savedViewsList };
    this._viewsChange$.next(this.savedViewsList);
  }

  clearSavedViews(): void {
    this.savedViewsList = {};
    this.individualViews = {};
    this.selectedViewId = {};
  }

  getSelectedViewIds(): { [workspaceId: string]: string } {
    return this.selectedViewId;
  }

  saveToView(newView: TSavedView, workspaceId: string): Observable<TSavedView> {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    return this.savedViewsProviderService.editView(requestWorkspaceId, newView).pipe(
      map((view) => fixView({ view })),
      tap((view) => {
        const prompt = this.translationPipe.transform('prompt_saved_view_updated');
        this.promptService.showSuccess(prompt);
        this.updateView(view, requestWorkspaceId);
      }),
      catchError((error) => {
        const prompt = this.translationPipe.transform('prompt_error');
        this.promptService.showError(prompt);

        return this.responseErrorService.handleRequestError(error, 'Failed to save to view');
      }),
    );
  }

  private fixWorkspaceViews(views: TSavedViews, workspaceId: string): TSavedViews {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    let fixedView: Partial<TSavedViews> = cloneDeep(views);

    if (!fixedView) {
      fixedView = {};
    }

    if (!fixedView.shared) {
      fixedView.shared = {
        views: [],
        sharedDefaultViewId: null,
        workspaceId: requestWorkspaceId,
      };
    }

    if (!fixedView.personal) {
      const user = this.userService.getUser();

      fixedView.personal = {
        workspaceId: requestWorkspaceId,
        userId: user.userId,
        defaultViewId: null,
        views: [],
      };
    }
    return fixedView as TSavedViews;
  }

  private setDefaultSelectedView(fixedView: Partial<TSavedViews>, workspaceId: string): void {
    const requestWorkspaceId = checkCustomWorkspaceId() || workspaceId;

    fixedView.personal.defaultViewId
      ? this.setSelectedViewId(requestWorkspaceId, fixedView.personal.defaultViewId)
      : this.setSelectedViewId(requestWorkspaceId, fixedView.shared.sharedDefaultViewId);
  }

  private setActiveViewId(viewId: string): void {
    this.activeViewId = viewId;
    this._viewsChange$.next(this.savedViewsList);
  }

  getActiveViewId(): string {
    return this.activeViewId;
  }
}
